/**
 * El catálogo de eventos del Registro, en el SPA.
 *
 * Etiquetas, categorías y severidades por defecto son del backend
 * (`bolloprint_shared.event_catalog`, servido por `GET /api/monitor/catalog`).
 * Aquí no se escribe ningún mapa de tipos a mano: el mapa viejo cubría dos
 * tipos de ~150, y un tipo nuevo salía en crudo sin que nada avisara.
 *
 * Es un almacén de módulo: el catálogo se pide UNA vez por sesión (los que lo
 * pidan a la vez comparten la misma promesa) y, si la petición falla, la
 * siguiente vuelve a intentarlo. Las lecturas son síncronas y nunca lanzan: sin
 * catálogo cargado —o con un tipo que no nombra, como la historia `legacy.*`—
 * devuelven el valor en crudo. Un componente que las use debe llamar a
 * `useEventCatalog()` para cargarlo y re-renderizar cuando llegue.
 */
import { useEffect, useSyncExternalStore } from 'react';
import type { ActivityStatus } from '@shared/components/RecentActivitySidebar';
import type { Tone } from '@shared/lib/tones';
import {
  getMonitorCatalog,
  type MonitorAlarmKind,
  type MonitorCatalog,
  type MonitorSeverity,
  type MonitorSource,
  type MonitorType,
} from '@/services/api';

interface CatalogState {
  catalog: MonitorCatalog | null;
  /** Último fallo de carga; vacío mientras carga o si llegó bien. */
  error: string;
}

// Un objeto nuevo en cada cambio: `useSyncExternalStore` compara por referencia.
let state: CatalogState = { catalog: null, error: '' };
let typeIndex = new Map<string, MonitorType>();
let categoryIndex = new Map<string, string>();
let alarmKindIndex = new Map<string, MonitorAlarmKind>();
let inflight: Promise<MonitorCatalog> | null = null;
const listeners = new Set<() => void>();

function setState(next: Partial<CatalogState>) {
  state = { ...state, ...next };
  listeners.forEach((notify) => notify());
}

/** Carga el catálogo (una vez). Rechaza si falla; la próxima llamada reintenta. */
export function loadEventCatalog(): Promise<MonitorCatalog> {
  if (state.catalog) return Promise.resolve(state.catalog);
  if (!inflight) {
    // Un reintento borra el aviso del fallo anterior mientras va.
    if (state.error) setState({ error: '' });
    inflight = getMonitorCatalog().then(
      (loaded) => {
        typeIndex = new Map(loaded.types.map((t) => [t.type, t]));
        categoryIndex = new Map(loaded.categories.map((c) => [c.key, c.label]));
        // Un backend anterior a las alarmas no manda la lista.
        alarmKindIndex = new Map((loaded.alarm_kinds ?? []).map((k) => [k.kind, k]));
        inflight = null;
        setState({ catalog: loaded, error: '' });
        return loaded;
      },
      (err: unknown) => {
        // Sin caché del fallo: la siguiente petición vuelve a intentarlo. Sí se
        // publica, para que la pantalla diga por qué sus filtros están vacíos.
        inflight = null;
        setState({ error: err instanceof Error && err.message ? err.message : 'No se pudo cargar el catálogo de eventos' });
        throw err;
      },
    );
  }
  return inflight;
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

const snapshot = () => state;

const retryLoad = () => {
  loadEventCatalog().catch(() => { /* el fallo ya queda en `error` */ });
};

/**
 * El catálogo (`null` mientras no ha llegado), el error de la última carga y
 * `retry` para volver a pedirlo. Dispara la carga al montar.
 */
export function useEventCatalog(): { catalog: MonitorCatalog | null; error: string; retry: () => void } {
  const current = useSyncExternalStore(subscribe, snapshot, snapshot);
  useEffect(() => {
    if (!state.catalog) retryLoad();
  }, []);
  return { catalog: current.catalog, error: current.error, retry: retryLoad };
}

/** Etiqueta en español de un `event_type`; el propio tipo si el catálogo no lo nombra. */
export function eventTypeLabel(eventType: string | null | undefined): string {
  if (!eventType) return '';
  return typeIndex.get(eventType)?.label ?? eventType;
}

/** Etiqueta de una categoría del catálogo; la clave si no la conoce. */
export function categoryLabel(key: string | null | undefined): string {
  if (!key) return '';
  return categoryIndex.get(key) ?? key;
}

/* ── Alarmas ──
 * Un tipo de alarma (`kind`) es la parte fija de su clave (`task.failed`,
 * `storage.quota`…); lo abren uno o más tipos de evento. */

/** Etiqueta de un tipo de alarma; la clave si el catálogo no lo nombra. */
export function alarmKindLabel(kind: string | null | undefined): string {
  if (!kind) return '';
  return alarmKindIndex.get(kind)?.label ?? kind;
}

/** El tipo de alarma tal como lo describe el catálogo, o undefined. */
export function alarmKindInfo(kind: string | null | undefined): MonitorAlarmKind | undefined {
  return kind ? alarmKindIndex.get(kind) : undefined;
}

/** ¿Este tipo de evento sólo avisa como alarma? (tipos de detector: la regla
 *  no puede pedir «cada evento»). */
export function isAlarmOnlyType(eventType: string | null | undefined): boolean {
  return !!eventType && typeIndex.get(eventType)?.alarm_only === true;
}

/** ¿Una fila de este tipo puede abrir una alarma? */
export function opensAlarm(eventType: string | null | undefined): boolean {
  return !!eventType && !!typeIndex.get(eventType)?.opens_alarm;
}

/* ── Severidad ──
 * El catálogo sólo trae las claves; el nombre y el tono son de la pantalla. La
 * que se pinta es la de la FILA, que puede no ser la del tipo. */

/** Orden de gravedad, de menos a más: el de los filtros. */
export const EVENT_SEVERITIES: readonly MonitorSeverity[] = ['info', 'success', 'warning', 'error', 'critical'];

const SEVERITY_LABEL: Readonly<Record<string, string>> = {
  info: 'Información',
  success: 'Correcto',
  warning: 'Aviso',
  error: 'Error',
  critical: 'Crítico',
};

const SEVERITY_TONE: Readonly<Record<string, Tone>> = {
  info: 'info',
  success: 'success',
  warning: 'warning',
  error: 'danger',
  critical: 'danger',
};

// `ActivityStatus` (@shared) no tiene `critical`: se pinta como un error.
const SEVERITY_ACTIVITY: Readonly<Record<string, ActivityStatus>> = {
  info: 'info',
  success: 'success',
  warning: 'warning',
  error: 'error',
  critical: 'error',
};

export function severityLabel(severity: string | null | undefined): string {
  if (!severity) return '';
  return SEVERITY_LABEL[severity] ?? severity;
}

export function severityTone(severity: string | null | undefined): Tone {
  return (severity && SEVERITY_TONE[severity]) || 'neutral';
}

export function severityActivityStatus(severity: string | null | undefined): ActivityStatus {
  return (severity && SEVERITY_ACTIVITY[severity]) || 'neutral';
}

/* ── Origen ── */

const SOURCE_LABEL: Readonly<Record<MonitorSource, string>> = {
  admin: 'Admin',
  tenant: 'Taller',
  worker: 'Worker',
  webhook: 'Webhook',
  scheduler: 'Tarea programada',
  web: 'Web',
};

export const EVENT_SOURCES = Object.keys(SOURCE_LABEL) as MonitorSource[];

export function sourceLabel(source: string | null | undefined): string {
  if (!source) return '';
  return (SOURCE_LABEL as Readonly<Record<string, string>>)[source] ?? source;
}
